import React, { useState, useEffect } from 'react';
import { api } from '../api';
import CreateDealModal from './CreateDealModal';
import CompanyAssignments from './CompanyAssignments';
import InlineEditField from './InlineEditField';
import DisqualifyModal from './DisqualifyModal';

const showToast = (message) => {
  const el = document.createElement('div');
  el.className = 'fixed bottom-4 right-4 bg-slate-700 text-slate-100 px-4 py-2 rounded text-sm z-50';
  el.textContent = message;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 3000);
};

export default function CompanyDetailPanel({ company, research, onClose, onResearchSingle, onRescore, onChange }) {
  if (!company) return null;
  const [researchLoading, setResearchLoading] = useState(false);
  const [researchStatusMsg, setResearchStatusMsg] = useState(null); // { kind: 'info'|'warn'|'error', text }
  const [knownDomain, setKnownDomain] = useState('');
  const [rescoreLoading, setRescoreLoading] = useState(false);
  const [dncLoading, setDncLoading] = useState(false);
  const [localDnc, setLocalDnc] = useState(!!company.dnc);
  const [enrichingContacts, setEnrichingContacts] = useState(false);
  const [showDealModal, setShowDealModal] = useState(false);
  // Quickview shows the three key details up top; everything else lives
  // behind this toggle (punch list: "move all other extra detail somewhere
  // else").
  const [showMore, setShowMore] = useState(false);
  const [bucketDefs, setBucketDefs] = useState([]);
  const [currentBucket, setCurrentBucket] = useState(company.bucket || '');
  const [bucketSaving, setBucketSaving] = useState(false);
  // Reasons for the disqualify modal, and the in-flight bucket pick that
  // triggered it. Both null when the modal isn't open.
  const [lostReasons, setLostReasons] = useState([]);
  const [disqualifyBucket, setDisqualifyBucket] = useState(null);

  useEffect(() => {
    api.getBucketDefinitions().then(defs => setBucketDefs(defs || [])).catch(() => {});
    // Loading reasons eagerly so the modal opens instantly on click.
    api.listLostReasons().then(setLostReasons).catch(() => setLostReasons([]));
  }, []);

  useEffect(() => {
    setCurrentBucket(company.bucket || '');
  }, [company.bucket]);

  const handleBucketChange = async (newBucket) => {
    // If the target bucket needs a reason, route through the modal flow
    // instead of writing directly. Modal calls /api/companies/{id}/disqualify
    // which does bucket+reason+optional DNC atomically.
    const def = bucketDefs.find(b => b.id === newBucket);
    if (newBucket && def?.requires_reason) {
      setDisqualifyBucket(def);
      return;
    }
    setBucketSaving(true);
    try {
      await api.setCompanyBucket(company.id, newBucket || null);
      setCurrentBucket(newBucket);
      showToast(newBucket ? `Moved to ${def?.label || newBucket}` : 'Removed from bucket');
      onChange?.();
    } catch (err) {
      showToast('Failed to set bucket');
    } finally {
      setBucketSaving(false);
    }
  };

  const handleDisqualifySaved = (result) => {
    setDisqualifyBucket(null);
    setCurrentBucket(disqualifyBucket?.id || '');
    if (result?.auto_dnc_applied) {
      setLocalDnc(true);
      const n = result?.cascaded_lead_count || 0;
      const cascadeMsg = n > 0 ? ` (${n} contact${n === 1 ? '' : 's'} also DNC'd)` : '';
      showToast(`Disqualified + DNC: ${result.reason_label}${cascadeMsg}`);
    } else {
      showToast(`Disqualified: ${result?.reason_label || 'saved'}`);
    }
    onChange?.();
  };

  const handleDealCreated = async (deal) => {
    setShowDealModal(false);
    showToast(`Created deal "${deal.name}"`);
    onChange?.();
  };

  const handleEnrichAllContacts = async () => {
    const ids = (company.contacts || []).map((c) => c.id).filter(Boolean);
    if (ids.length === 0) {
      showToast('No contacts to enrich');
      return;
    }
    if (!confirm(`Queue enrichment for ${ids.length} contact(s) at ${company.name}?`)) return;
    setEnrichingContacts(true);
    try {
      await api.bulkEnrich(ids);
      showToast(`Enrichment queued for ${ids.length} contacts`);
      onChange?.();
    } catch (err) {
      showToast('Enrichment failed to queue');
    } finally {
      setEnrichingContacts(false);
    }
  };

  const handleDncToggle = async () => {
    setDncLoading(true);
    try {
      if (localDnc) {
        if (!confirm(`Remove DNC from ${company.name}? All its leads will also be restored.`)) {
          setDncLoading(false);
          return;
        }
        await api.unmarkCompanyDnc(company.id);
        setLocalDnc(false);
        showToast('Company DNC removed');
      } else {
        const reason = window.prompt(
          `Mark ${company.name} as Do Not Contact. All linked leads will also be flagged. Optional reason:`,
          ''
        );
        if (reason === null) {
          setDncLoading(false);
          return;
        }
        const result = await api.markCompanyDnc(company.id, reason || null);
        setLocalDnc(true);
        showToast(`Marked DNC (${result?.cascaded_leads ?? 0} leads cascaded)`);
      }
      onChange?.();
    } catch (err) {
      showToast('DNC update failed');
    } finally {
      setDncLoading(false);
    }
  };

  const fitColor = (fit) => ({
    high: 'text-emerald-400', medium: 'text-yellow-400',
    low: 'text-red-400'
  }[fit] || 'text-slate-400');

  const statusBadgeColor = (status) => ({
    pending: 'bg-slate-600 text-slate-200',
    researching: 'bg-indigo-600 text-indigo-100',
    done: 'bg-emerald-600 text-emerald-100',
    failed: 'bg-red-600 text-red-100'
  }[status] || 'bg-slate-600 text-slate-200');

  const handleResearchClick = async () => {
    setResearchLoading(true);
    setResearchStatusMsg({ kind: 'info', text: 'Resolving website, scraping, fetching news… this can take 30-60s.' });
    try {
      // onResearchSingle resolves to the research+score payload the parent just refetched.
      const payload = await onResearchSingle(company.id, knownDomain.trim() || null);

      // Inspect what actually came back so we can tell the user if it's empty.
      const r = payload?.research || payload || {};
      const hasWebsite = !!r.website;
      const hasDescription = !!r.description;
      const hasNews = Array.isArray(r.recent_news) && r.recent_news.length > 0;
      const hasGithub = !!r.github_org;
      const anything = hasWebsite || hasDescription || hasNews || hasGithub;

      if (!anything) {
        setResearchStatusMsg({
          kind: 'warn',
          text: 'Research returned empty. No website could be resolved — try pasting a known domain below and retry.',
        });
        showToast('Research returned empty');
      } else {
        const parts = [];
        if (hasWebsite) parts.push('website');
        if (hasDescription) parts.push('description');
        if (hasNews) parts.push(`${r.recent_news.length} news`);
        if (hasGithub) parts.push('github');
        setResearchStatusMsg({ kind: 'ok', text: `Research complete (${parts.join(', ')}).` });
        showToast('Research complete');
      }
    } catch (err) {
      const raw = (err?.message || '').replace('API error:', '').trim();
      setResearchStatusMsg({
        kind: 'error',
        text: raw || 'Research failed with an unknown error.',
      });
      showToast('Research failed');
    } finally {
      setResearchLoading(false);
    }
  };

  const handleRescoreClick = async () => {
    setRescoreLoading(true);
    try {
      await onRescore(company.id);
      showToast('Scoring started');
    } catch (err) {
      showToast('Scoring failed');
    } finally {
      setRescoreLoading(false);
    }
  };

  const isPending = research?.research_status === 'pending' || !research?.research_status;
  const score = research?.score;

  // Save handler for the inline Identity editor. Touches one field at a time
  // (the field component passes the new value); on success we ask the parent
  // to refresh so the row updates everywhere (company list + logo, etc.).
  const handleIdentitySave = async (field, value) => {
    try {
      await api.updateCompany(company.id, { [field]: value });
      onChange?.();
    } catch (e) {
      // The backend returns 409 with a structured detail when linkedin_url
      // collides; surface that helpfully.
      let msg = e?.message || 'Save failed';
      try {
        const data = JSON.parse(e.message.split('HTTP ')[1].split(': ')[1] || '{}');
        if (data?.error === 'linkedin_url_already_used') {
          msg = `Already used by ${data.owned_by_company_name}`;
        }
      } catch {}
      throw new Error(msg);
    }
  };

  // Punch-list quickview: surface the single best "existing contact" — a
  // person we can actually open on LinkedIn. Prefer warm-intro contacts,
  // then highest ICP score.
  const linkedInUrlFor = (c) => {
    const li = c.linkedin_public_url
      || (c.linkedin_url && /\/in\//i.test(c.linkedin_url) ? c.linkedin_url : null)
      || (c.linkedin_url_slug ? `https://www.linkedin.com/in/${c.linkedin_url_slug}` : null);
    if (!li) return null;
    return /^https?:\/\//i.test(li) ? li : `https://${li}`;
  };
  const keyContact = (company.contacts || [])
    .slice()
    .sort((a, b) => (b.has_intro || 0) - (a.has_intro || 0) || (b.icp_score || 0) - (a.icp_score || 0))
    .find(c => linkedInUrlFor(c)) || (company.contacts || [])[0] || null;
  const keyContactLi = keyContact ? linkedInUrlFor(keyContact) : null;

  return (
    <div className="w-96 bg-slate-800 border-l border-slate-700 h-full overflow-y-auto scrollable-content p-6 flex-shrink-0">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-slate-100 truncate">{company.name}</h2>
        <div className="flex items-center gap-2">
          {research?.research_status && (
            <span className={`text-xs px-2 py-1 rounded font-medium ${statusBadgeColor(research.research_status)}`}>
              {research.research_status}
            </span>
          )}
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200 text-xl">&times;</button>
        </div>
      </div>

      {/* Key details (punch list): company size, best existing contact with
          a clickable LinkedIn link, and Create Deal — the three things a rep
          needs at a glance. Everything else is behind "More detail". */}
      <div className="mb-5 bg-slate-900/60 border border-slate-700 rounded-lg p-4">
        <div className="flex justify-between text-sm mb-3">
          <span className="text-slate-400">Company size</span>
          <span className="text-slate-100 font-medium">{company.size || 'Unknown'}</span>
        </div>
        <div className="mb-3">
          <div className="text-slate-400 text-sm mb-1">Existing contact</div>
          {keyContact ? (
            <div className="flex items-center justify-between gap-2 bg-slate-800/80 border border-slate-700 rounded p-2.5">
              <div className="min-w-0">
                <div className="text-sm text-slate-100 font-medium truncate">{keyContact.full_name || 'Unknown'}</div>
                <div className="text-xs text-slate-500 truncate">{keyContact.title || 'No title'}</div>
                {keyContact.intro_names && (
                  <div className="text-[10px] text-emerald-400 truncate">via {keyContact.intro_names}</div>
                )}
              </div>
              {keyContactLi ? (
                <a
                  href={keyContactLi}
                  target="_blank" rel="noopener noreferrer"
                  className="flex-shrink-0 px-3 py-1.5 bg-sky-600 hover:bg-sky-500 text-white rounded text-xs font-semibold transition-colors"
                  title="Open LinkedIn profile"
                >
                  LinkedIn ↗
                </a>
              ) : (
                <span className="flex-shrink-0 text-[11px] text-slate-600">no LinkedIn</span>
              )}
            </div>
          ) : (
            <div className="text-sm text-slate-500">N/A — no contact yet</div>
          )}
        </div>
        <button
          onClick={() => setShowDealModal(true)}
          className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded text-sm font-semibold transition-colors"
          title="Create a new deal for this company"
        >
          + Create Deal
        </button>
      </div>

      {/* Deals linked to this company — nurture info logged on a deal shows
          here too, so the master list and the pipeline stay in sync. */}
      {(company.deals || []).length > 0 && (
        <div className="mb-5">
          <h3 className="text-sm font-semibold text-slate-300 mb-2">Deals ({company.deals.length})</h3>
          <div className="space-y-1.5">
            {company.deals.map(d => (
              <div key={d.id} className="bg-slate-700/40 border border-slate-700 rounded p-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-slate-100 font-medium truncate">{d.name}</span>
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0"
                    style={{
                      backgroundColor: (d.stage_color || '#475569') + '33',
                      color: d.stage_color || '#94a3b8',
                    }}
                  >
                    {d.stage_label || d.stage}
                  </span>
                </div>
                {d.latest_note && (
                  <div className="text-xs text-slate-400 mt-1 line-clamp-2 whitespace-pre-wrap" title={d.latest_note}>
                    {d.latest_note}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bucket selector */}
      <div className="mb-5">
        <label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1.5">Bucket</label>
        <div className="flex flex-wrap gap-1.5">
          {bucketDefs.map(b => (
            <button
              key={b.id}
              onClick={() => handleBucketChange(currentBucket === b.id ? '' : b.id)}
              disabled={bucketSaving}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                currentBucket === b.id
                  ? 'text-white shadow-sm'
                  : 'bg-slate-800 text-slate-400 hover:text-slate-200 hover:bg-slate-700'
              }`}
              style={currentBucket === b.id ? { backgroundColor: b.color || '#6366f1' } : {}}
            >
              {b.emoji ? `${b.emoji} ` : ''}{b.label}
            </button>
          ))}
          {bucketDefs.length === 0 && (
            <span className="text-xs text-slate-600">No buckets defined — add them in Settings</span>
          )}
        </div>
      </div>

      {/* DNC banner + toggle */}
      {localDnc && (
        <div className="mb-4 bg-red-900/30 border border-red-700/50 rounded p-3 text-sm text-red-300">
          <span className="font-semibold">Do Not Contact</span> — this company and all its leads are suppressed.
        </div>
      )}

      {/* Everything beyond the key details is tucked away here. */}
      <button
        onClick={() => setShowMore(m => !m)}
        className="w-full mb-5 px-4 py-2 bg-slate-700/60 hover:bg-slate-700 text-slate-300 rounded text-sm font-medium transition-colors"
      >
        {showMore ? 'Hide extra detail ▲' : 'More detail — research, contacts, signals ▼'}
      </button>

      {showMore && (<>
      <button
        onClick={handleDncToggle}
        disabled={dncLoading}
        className={`w-full mb-6 px-4 py-2 rounded text-sm font-medium transition-colors disabled:opacity-50 ${
          localDnc
            ? 'bg-slate-700 hover:bg-green-700 text-slate-200'
            : 'bg-red-900/40 hover:bg-red-800/60 text-red-300 border border-red-800/50'
        }`}
      >
        {dncLoading ? '...' : localDnc ? 'Remove DNC' : 'Mark company as Do Not Contact'}
      </button>

      {/* Identity — click any field to edit. These feed the company logo,
          deal-modal company row, and (for linkedin_url) the canonical join
          key that unifies leads across ingest paths. */}
      <div className="mb-6 bg-slate-900/40 border border-slate-700 rounded p-4">
        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Identity</div>
        <InlineEditField
          label="Name"
          value={company.name}
          placeholder="Company name"
          onSave={(v) => handleIdentitySave('name', v)}
        />
        <InlineEditField
          label="Website"
          value={company.website}
          placeholder="acme.com"
          onSave={(v) => handleIdentitySave('website', v)}
          renderValue={(v) => v
            ? <a href={v.startsWith('http') ? v : `https://${v}`}
                 target="_blank" rel="noopener noreferrer"
                 onClick={(e) => e.stopPropagation()}
                 className="text-indigo-300 hover:text-indigo-200 underline-offset-2 hover:underline">
                {v.replace(/^https?:\/\//, '').replace(/\/$/, '')}
              </a>
            : <span className="text-slate-600 italic">click to set</span>}
        />
        <InlineEditField
          label="LinkedIn"
          value={company.linkedin_url}
          placeholder="linkedin.com/company/acme"
          onSave={(v) => handleIdentitySave('linkedin_url', v)}
          renderValue={(v) => v
            ? <a href={v.startsWith('http') ? v : `https://${v}`}
                 target="_blank" rel="noopener noreferrer"
                 onClick={(e) => e.stopPropagation()}
                 className="text-indigo-300 hover:text-indigo-200 underline-offset-2 hover:underline">
                {v.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
              </a>
            : <span className="text-slate-600 italic">click to set</span>}
        />
        <InlineEditField
          label="Industry"
          value={company.industry}
          placeholder="e.g. SaaS"
          onSave={(v) => handleIdentitySave('industry', v)}
        />
        <InlineEditField
          label="Size"
          value={company.size}
          placeholder="e.g. 50-200"
          onSave={(v) => handleIdentitySave('size', v)}
        />
        <InlineEditField
          label="Location"
          value={company.location}
          placeholder="e.g. San Francisco"
          onSave={(v) => handleIdentitySave('location', v)}
        />
      </div>

      {/* Research controls — always visible so users can retry / provide a known domain */}
      <div className="mb-6 bg-indigo-600/10 border border-indigo-600/30 rounded p-4">
        {isPending ? (
          <p className="text-sm text-indigo-300 mb-3">No research yet</p>
        ) : (
          <p className="text-sm text-indigo-300 mb-3">Re-run research</p>
        )}

        <input
          type="text"
          value={knownDomain}
          onChange={(e) => setKnownDomain(e.target.value)}
          placeholder="Optional: paste known domain (e.g. acme.com)"
          className="w-full mb-3 px-3 py-1.5 bg-slate-800 border border-slate-700 rounded text-xs text-slate-200 placeholder-slate-500"
          disabled={researchLoading}
        />

        <button
          onClick={handleResearchClick}
          disabled={researchLoading}
          className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-sm font-medium transition-colors disabled:opacity-50"
        >
          {researchLoading ? 'Researching… (30-60s)' : isPending ? 'Research this company' : 'Re-research'}
        </button>

        {researchStatusMsg && (
          <div
            className={`mt-3 text-xs rounded p-2 ${
              researchStatusMsg.kind === 'error'
                ? 'bg-red-900/30 border border-red-700/50 text-red-300'
                : researchStatusMsg.kind === 'warn'
                ? 'bg-amber-900/30 border border-amber-700/50 text-amber-300'
                : researchStatusMsg.kind === 'ok'
                ? 'bg-emerald-900/30 border border-emerald-700/50 text-emerald-300'
                : 'bg-slate-800 border border-slate-700 text-slate-300'
            }`}
          >
            {researchStatusMsg.text}
          </div>
        )}
      </div>

      {isPending ? null : (
        <>
          {/* Score box — always visible when research is done */}
          <div className="mb-6 bg-slate-700/50 rounded p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-semibold text-slate-300">Score</span>
              {score ? (
                <span className="text-2xl font-bold text-emerald-400">{score.score}</span>
              ) : (
                <span className="text-xs text-slate-500 italic">Not scored yet</span>
              )}
            </div>
            {score ? (
              <>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-400">Fit</span>
                  <span className={`capitalize font-medium ${fitColor(score.fit)}`}>{score.fit}</span>
                </div>
                {(score.best_product_name || score.best_product_id) && (
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-400">Best-fit product</span>
                    <span className="text-indigo-300 font-medium">{score.best_product_name || score.best_product_id}</span>
                  </div>
                )}
                {score.reasoning && (
                  <div className="mt-3 bg-slate-800/60 border border-slate-700 rounded p-3">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Score justification</div>
                    <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">{score.reasoning}</p>
                  </div>
                )}
                {Array.isArray(score.per_product) && score.per_product.length > 0 && (
                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">Per-product fit</div>
                    <div className="space-y-1">
                      {score.per_product.map((pp, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs bg-slate-800/40 rounded px-2 py-1.5">
                          <span className={`capitalize font-medium flex-shrink-0 w-14 ${fitColor(pp.fit)}`}>{pp.fit}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-slate-300 font-medium">{pp.product_id}</div>
                            {pp.rationale && <div className="text-slate-400 mt-0.5">{pp.rationale}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-xs text-slate-400">
                Research is done but this company hasn't been scored against your products yet. Click <span className="text-indigo-300">Re-score</span> below to pick the best-fit product and generate a justification.
              </p>
            )}
          </div>

          {/* Raw research fields */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-300 mb-2">Research</h3>
            <div className="bg-slate-700/30 rounded p-3 space-y-2 text-sm">
              {research?.website ? (
                <div className="flex justify-between gap-2">
                  <span className="text-slate-400 flex-shrink-0">Website</span>
                  <a
                    href={research.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-400 hover:text-indigo-300 truncate"
                    title={research.website}
                  >
                    {research.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  </a>
                </div>
              ) : (
                <div className="flex justify-between"><span className="text-slate-400">Website</span><span className="text-slate-500 italic">not resolved</span></div>
              )}
              {research?.description && (
                <div>
                  <div className="text-slate-400 mb-1">Description</div>
                  <p className="text-slate-200 text-xs leading-relaxed">{research.description}</p>
                </div>
              )}
              {research?.value_prop && (
                <div>
                  <div className="text-slate-400 mb-1">Value prop</div>
                  <p className="text-slate-200 text-xs leading-relaxed italic">{research.value_prop}</p>
                </div>
              )}
              {research?.industry && (
                <div className="flex justify-between"><span className="text-slate-400">Industry</span><span className="text-slate-200">{research.industry}</span></div>
              )}
              {research?.target_market && (
                <div className="flex justify-between gap-2"><span className="text-slate-400 flex-shrink-0">Target market</span><span className="text-slate-200 text-right">{research.target_market}</span></div>
              )}
              {research?.hq_location && (
                <div className="flex justify-between"><span className="text-slate-400">HQ</span><span className="text-slate-200">{research.hq_location}</span></div>
              )}
              {research?.employee_count_range && (
                <div className="flex justify-between"><span className="text-slate-400">Employees</span><span className="text-slate-200">{research.employee_count_range}</span></div>
              )}
              {research?.revenue_range && (
                <div className="flex justify-between"><span className="text-slate-400">Revenue</span><span className="text-slate-200">{research.revenue_range}</span></div>
              )}
              {research?.founded_year && (
                <div className="flex justify-between"><span className="text-slate-400">Founded</span><span className="text-slate-200">{research.founded_year}</span></div>
              )}
              {research?.funding_stage && (
                <div className="flex justify-between"><span className="text-slate-400">Funding</span><span className="text-slate-200">{research.funding_stage}</span></div>
              )}
              {research?.github_org && (
                <div className="flex justify-between gap-2">
                  <span className="text-slate-400">GitHub</span>
                  <a
                    href={`https://github.com/${research.github_org}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-400 hover:text-indigo-300"
                  >
                    @{research.github_org}
                  </a>
                </div>
              )}
              {Array.isArray(research?.tech_stack) && research.tech_stack.length > 0 && (
                <div>
                  <div className="text-slate-400 mb-1">Tech stack</div>
                  <div className="flex flex-wrap gap-1">
                    {research.tech_stack.map((t, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 bg-slate-600/60 text-slate-200 rounded">{t}</span>
                    ))}
                  </div>
                </div>
              )}
              {Array.isArray(research?.products) && research.products.length > 0 && (
                <div>
                  <div className="text-slate-400 mb-1">Products</div>
                  <div className="flex flex-wrap gap-1">
                    {research.products.map((p, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 bg-indigo-900/60 text-indigo-200 rounded">{p}</span>
                    ))}
                  </div>
                </div>
              )}
              {Array.isArray(research?.customers) && research.customers.length > 0 && (
                <div>
                  <div className="text-slate-400 mb-1">Named customers</div>
                  <div className="flex flex-wrap gap-1">
                    {research.customers.map((c, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 bg-emerald-900/50 text-emerald-200 rounded">{c}</span>
                    ))}
                  </div>
                </div>
              )}
              {Array.isArray(research?.key_people) && research.key_people.length > 0 && (
                <div>
                  <div className="text-slate-400 mb-1">Key people</div>
                  <ul className="text-xs text-slate-200 space-y-0.5">
                    {research.key_people.map((p, i) => (<li key={i}>• {p}</li>))}
                  </ul>
                </div>
              )}
              {research?.last_researched_at && (
                <div className="flex justify-between pt-1 border-t border-slate-700/50">
                  <span className="text-slate-500 text-xs">Last researched</span>
                  <span className="text-slate-500 text-xs">{new Date(research.last_researched_at).toLocaleString()}</span>
                </div>
              )}
              {!research?.website && !research?.description && !research?.hq_location && !research?.github_org && (!research?.tech_stack || research.tech_stack.length === 0) && (
                <p className="text-xs text-slate-500 italic">Research completed but returned no data. Try pasting a known domain above and re-researching.</p>
              )}
            </div>
          </div>

          {research?.buying_signals?.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-emerald-400 mb-2">Buying Signals</h3>
              <ul className="space-y-1 text-sm">
                {research.buying_signals.map((sig, i) => (
                  <li key={i} className="text-slate-300 flex items-start gap-2">
                    <span className="text-emerald-400 mt-0.5">•</span>
                    <span>{sig}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {research?.disqualifiers?.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-red-400 mb-2">Disqualifiers</h3>
              <ul className="space-y-1 text-sm">
                {research.disqualifiers.map((dis, i) => (
                  <li key={i} className="text-slate-300 flex items-start gap-2">
                    <span className="text-red-400 mt-0.5">•</span>
                    <span>{dis}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {research?.recent_news?.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-300 mb-2">Recent News</h3>
              <div className="space-y-2">
                {research.recent_news.slice(0, 5).map((news, i) => (
                  <a
                    key={i}
                    href={news.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-xs text-indigo-400 hover:text-indigo-300 line-clamp-2"
                    title={news.headline}
                  >
                    {news.headline}
                  </a>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={handleRescoreClick}
            disabled={rescoreLoading}
            className="w-full px-4 py-2 bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-400 rounded text-sm font-medium transition-colors disabled:opacity-50"
          >
            {rescoreLoading ? 'Scoring...' : 'Re-score'}
          </button>

        </>
      )}

      </>)}

      {showDealModal && (
        <CreateDealModal
          company={company}
          onClose={() => setShowDealModal(false)}
          onCreated={handleDealCreated}
        />
      )}

      {disqualifyBucket && (
        <DisqualifyModal
          company={company}
          bucket={disqualifyBucket}
          reasons={lostReasons}
          onCancel={() => setDisqualifyBucket(null)}
          onSaved={handleDisqualifySaved}
        />
      )}

      {showMore && (<>
      <div className="mb-6">
        <CompanyAssignments companyId={company.id} />
      </div>

      <div className="space-y-2 mb-6 text-sm mt-6">
        {company.industry && <div className="flex justify-between"><span className="text-slate-400">Industry</span><span className="text-slate-200">{company.industry}</span></div>}
        {company.size && <div className="flex justify-between"><span className="text-slate-400">Size</span><span className="text-slate-200">{company.size}</span></div>}
        {company.location && <div className="flex justify-between"><span className="text-slate-400">Location</span><span className="text-slate-200">{company.location}</span></div>}
      </div>

      {company.product_fits?.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-slate-300 mb-2">Product Fit</h3>
          <div className="space-y-1">
            {company.product_fits.map((pf, i) => (
              <div key={i} className="flex justify-between items-center py-1">
                <span className="text-sm text-slate-300">{pf.product_name}</span>
                <span className={`text-sm font-medium capitalize ${fitColor(pf.fit)}`}>{pf.fit}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Contacts */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-slate-300">Contacts ({company.contacts?.length || 0})</h3>
          {(company.contacts?.length || 0) > 0 && (
            <button
              onClick={handleEnrichAllContacts}
              disabled={enrichingContacts}
              className="text-xs px-2 py-1 bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-300 rounded font-medium transition-colors disabled:opacity-50"
              title="Run enrichment waterfall on every contact at this company"
            >
              {enrichingContacts ? 'Queuing…' : 'Enrich all'}
            </button>
          )}
        </div>
        <div className="space-y-1.5">
          {(company.contacts || []).map(c => (
            <div key={c.id} className="bg-slate-700/50 rounded p-2.5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm text-slate-100 font-medium truncate">{c.full_name || 'Unknown'}</span>
                  {c.source === 'connection_only' && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-600/60 text-slate-400 flex-shrink-0">Connection</span>
                  )}
                  {/* Signal badges with hover tooltip showing the source/target.
                      The backend's signal_targets dict maps signal_type → the
                      target_label of the most recent matching signal (e.g.
                      "Followed Alpha Level page" or "Liked post by Josh Kim").
                      We pick the first matching target_label per badge type. */}
                  {(() => {
                    const targets = c.signal_targets || {};
                    const pickTarget = (...types) => {
                      for (const t of types) if (targets[t]) return targets[t];
                      return null;
                    };
                    const followTarget  = pickTarget('company_follower');
                    const likeTarget    = pickTarget('personal_post_like', 'company_post_like', 'post_like');
                    const commentTarget = pickTarget('post_comment');
                    const reshareTarget = pickTarget('post_reshare');
                    const types = c.signal_types || '';
                    return (
                      <>
                        {types.includes('company_follower') && (
                          <span title={followTarget ? `Followed ${followTarget}` : 'Followed'}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-400 flex-shrink-0 cursor-help">Followed</span>
                        )}
                        {(types.includes('personal_post_like') || types.includes('company_post_like') || types.includes('post_like')) && (
                          <span title={likeTarget ? `Liked ${likeTarget}` : 'Liked a post'}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-400 flex-shrink-0 cursor-help">Liked</span>
                        )}
                        {types.includes('post_comment') && (
                          <span title={commentTarget ? `Commented on ${commentTarget}` : 'Commented'}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-400 flex-shrink-0 cursor-help">Commented</span>
                        )}
                        {types.includes('post_reshare') && (
                          <span title={reshareTarget ? `Reshared ${reshareTarget}` : 'Reshared'}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-pink-500/10 text-pink-400 flex-shrink-0 cursor-help">Reshared</span>
                        )}
                      </>
                    );
                  })()}
                  {c.connection_degree && (
                    <span className="text-[10px] text-slate-500 flex-shrink-0">{c.connection_degree}</span>
                  )}
                </div>
                {c.icp_score != null && (
                  <span className={`text-[11px] font-bold tabular-nums flex-shrink-0 ${
                    c.icp_score >= 80 ? 'text-emerald-400' :
                    c.icp_score >= 60 ? 'text-amber-400' :
                    c.icp_score >= 40 ? 'text-sky-400' : 'text-slate-500'
                  }`}>
                    {c.icp_score}
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">{c.title || 'No title'}</div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {c.intro_names && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400">
                    via {c.intro_names}
                  </span>
                )}
                {/* Direct LinkedIn profile link. Prefer the clean public /in/ URL;
                    fall back to the raw linkedin_url only if it's already an /in/
                    link (Sales Nav /sales/lead/ URLs aren't publicly reachable);
                    last resort, rebuild from the normalized slug. */}
                {(() => {
                  const li = c.linkedin_public_url
                    || (c.linkedin_url && /\/in\//i.test(c.linkedin_url) ? c.linkedin_url : null)
                    || (c.linkedin_url_slug ? `https://www.linkedin.com/in/${c.linkedin_url_slug}` : null);
                  if (!li) return null;
                  const href = /^https?:\/\//i.test(li) ? li : `https://${li}`;
                  return (
                    <a href={href} target="_blank" rel="noopener noreferrer"
                       title="Open LinkedIn profile"
                       className="text-[11px] text-sky-400 hover:text-sky-300 transition-colors">
                      LinkedIn ↗
                    </a>
                  );
                })()}
                {c.email && (
                  <a href={`mailto:${c.email}`} className="text-[11px] text-indigo-400 hover:text-indigo-300 transition-colors truncate">
                    {c.email}
                  </a>
                )}
                {c.phone && (
                  <span className="text-[11px] text-slate-500">{c.phone}</span>
                )}
              </div>
            </div>
          ))}
          {(!company.contacts || company.contacts.length === 0) && <p className="text-sm text-slate-500">No contacts linked</p>}
        </div>
      </div>

      {/* Signals — individual followers/engagers */}
      {company.signals && company.signals.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-slate-300 mb-2">Engagement signals ({company.signals.length})</h3>
          <div className="space-y-1">
            {company.signals.map((sig, i) => {
              const typeLabel = sig.signal_type === 'company_follower' ? 'Followed' :
                sig.signal_type === 'personal_post_like' ? 'Liked post' :
                sig.signal_type === 'company_post_like' ? 'Liked post' : sig.signal_type;
              const typeColor = sig.signal_type === 'company_follower' ? 'text-sky-400 bg-sky-500/10' :
                'text-violet-400 bg-violet-500/10';
              return (
                <div key={sig.id || i} className="flex items-center gap-2 text-xs py-1.5 px-2 rounded bg-slate-700/30">
                  <span
                    className="text-slate-200 truncate flex-1"
                    title={[sig.linkedin_name, sig.linkedin_headline].filter(Boolean).join(' — ')}
                  >
                    {sig.linkedin_name || 'Unknown'}
                  </span>
                  {sig.linkedin_headline && (
                    <span className="text-[10px] text-slate-500 truncate max-w-[120px] hidden sm:inline" title={sig.linkedin_headline}>
                      {sig.linkedin_headline}
                    </span>
                  )}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 ${typeColor}`}>{typeLabel}</span>
                  {sig.captured_at && (
                    <span className="text-[10px] text-slate-600 flex-shrink-0">{sig.captured_at.split('T')[0]}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}
